import { StyledInput } from 'components/Common/FormikInput';
import { StyledDatePicker } from 'components/CustomDatepicker';
import { useFormikContext } from 'formik';

import { isUserOperator } from 'helpers/jwt_helper';
import { DEFAULT_POINTS } from 'helpers/styles/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Label } from 'reactstrap';
import { useDescribeToEstimate } from './helpers';
import { addDays, subDays } from 'date-fns';

export const ApplicantDates = ({ index, orderTypeFieldsSettings }) => {
  const formik = useFormikContext();
  const { t } = useTranslation();
  const applicantData = formik.values.applicants?.[index];
  const isDisabledFields = !formik.values.orderDetail?.isEditable;
  const { minimalEstimate } = useDescribeToEstimate(applicantData);

  const isD1GtOrEqD2 = (date1, date2) => {
    if (date1?.getFullYear() > date2?.getFullYear()) {
      return true;
    } else if (date1?.getMonth() > date2?.getMonth()) {
      return true;
    } else {
      return date1?.getDate() >= date2?.getDate();
    }
  };

  const datesMatch = isD1GtOrEqD2(
    formik.values.applicants?.[index]?.documentsToApplicantEstimatedAt,
    minimalEstimate,
  );

  return (
    <div className="mt-4">
      {orderTypeFieldsSettings?.enableFirstDateOfEntry && (
        <FormGroup row className="mt-4 align-items-center">
          <Col {...DEFAULT_POINTS.label.xs} sm={5} xxl={3}>
            <Label>{t('order.firstEntry')}:</Label>
          </Col>

          <Col {...DEFAULT_POINTS.input.xs} sm={6} lg={4}>
            <StyledDatePicker
              onChange={date => {
                formik.setFieldValue(
                  `applicants[${index}].firstDateEntry`,
                  date,
                );
                // Reset documents to applicant estimated at, as it should be < first date entry
                formik.setFieldValue(
                  `applicants[${index}].documentsToApplicantEstimatedAt`,
                  null,
                );
              }}
              disabled={isDisabledFields}
              selected={formik.values.applicants?.[index]?.firstDateEntry}
              placeholder={t('common.datePlaceholder')}
              placeholderText={t('common.datePlaceholder')}
              minDate={minimalEstimate}
              maxDate={addDays(minimalEstimate, 90)}
            />
          </Col>
        </FormGroup>
      )}

      {/* Documents to Applicant (When do you need your documents back?) */}
      {orderTypeFieldsSettings?.enableDocumentsToApplicantEstimatedAt && (
        <FormGroup row className="mb-4 align-items-center">
          <Col sm={5} xxl={3}>
            <Label className="m-0">
              {t('order.documentsToApplicantEstimatedAt')}?
            </Label>
          </Col>

          <Col xs={12} sm={6} lg={4}>
            <StyledDatePicker
              onChange={date =>
                formik.setFieldValue(
                  `applicants[${index}].documentsToApplicantEstimatedAt`,
                  date,
                )
              }
              minDate={
                formik.values.applicants?.[index]?.statusApplicant ===
                  'draft' && minimalEstimate
                  ? minimalEstimate
                  : undefined
              }
              maxDate={
                orderTypeFieldsSettings?.enableFirstDateOfEntry &&
                applicantData.statusApplicant === 'draft'
                  ? applicantData?.firstDateEntry
                  : addDays(minimalEstimate, 90)
              }
              selected={
                formik.values.applicants?.[index]
                  ?.documentsToApplicantEstimatedAt
              }
              placeholder={t('common.datePlaceholder')}
              disabled={
                isDisabledFields ||
                (orderTypeFieldsSettings?.enableFirstDateOfEntry &&
                  !formik.values.applicants?.[index]?.firstDateEntry)
              }
              placeholderText={t('common.datePlaceholder')}
              invalid={
                formik.errors.applicants?.[index]
                  ?.documentsToApplicantEstimatedAt ||
                (applicantData.statusApplicant === 'draft' && !datesMatch)
              }
            />
            {orderTypeFieldsSettings?.enableFirstDateOfEntry &&
              !formik.values.applicants?.[index]?.firstDateEntry && (
                <span
                  className="d-inline-flex align-items-center gap-1 text-warning fw-semibold mt-1"
                  style={{ fontSize: '11px' }}
                >
                  <span>⚠️</span> {t('order.firstEntryIsRequired')}
                </span>
              )}
          </Col>
        </FormGroup>
      )}

      {/* Documents to Consulate  */}

      {isUserOperator &&
        orderTypeFieldsSettings?.enableDocumentsToConsEstimatedAt && (
          <FormGroup row className="mb-4 align-items-center">
            <Col sm={5} xxl={3}>
              <Label className="m-0">
                {t('order.documentsToConsulateEstimatedAt')}
              </Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledDatePicker
                onChange={date =>
                  formik.setFieldValue(
                    `applicants[${index}].documentsToConsulateEstimatedAt`,
                    date,
                  )
                }
                selected={
                  formik.values.applicants?.[index]
                    ?.documentsToConsulateEstimatedAt
                }
                placeholder={t('common.datePlaceholder')}
                disabled={isDisabledFields}
                placeholderText={t('common.datePlaceholder')}
                minDate={minimalEstimate}
                invalid={
                  formik.errors.applicants?.[index]
                    ?.documentsToConsulateEstimatedAt
                }
              />
            </Col>
          </FormGroup>
        )}

      {isUserOperator &&
        orderTypeFieldsSettings?.enableDocumentsToConsActualAt && (
          <FormGroup row className="mb-4 align-items-center">
            <Col sm={5} xxl={3}>
              <Label className="m-0">
                {t('order.documentsToConsulateActualAt')}
              </Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledDatePicker
                onChange={date =>
                  formik.setFieldValue(
                    `applicants[${index}].documentsToConsulateActualAt`,
                    date,
                  )
                }
                selected={
                  formik.values.applicants?.[index]
                    ?.documentsToConsulateActualAt
                }
                placeholder={t('common.datePlaceholder')}
                disabled={isDisabledFields}
                placeholderText={t('common.datePlaceholder')}
                invalid={
                  formik.errors.applicants?.[index]
                    ?.documentsToConsulateActualAt
                }
              />
            </Col>
          </FormGroup>
        )}

      {/* Documents from Consulate  */}
      {isUserOperator &&
        orderTypeFieldsSettings?.enableDocumentsFromConsEstimatedAt && (
          <FormGroup row className="mb-4 align-items-center">
            <Col sm={5} xxl={3}>
              <Label className="m-0">
                {t('order.documentsFromConsulateEstimatedAt')}
              </Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledDatePicker
                onChange={date =>
                  formik.setFieldValue(
                    `applicants[${index}].documentsFromConsulateEstimatedAt`,
                    date,
                  )
                }
                selected={
                  formik.values.applicants?.[index]
                    ?.documentsFromConsulateEstimatedAt
                }
                placeholder={t('common.datePlaceholder')}
                disabled={isDisabledFields}
                placeholderText={t('common.datePlaceholder')}
                invalid={
                  formik.errors.applicants?.[index]
                    ?.documentsFromConsulateEstimatedAt
                }
                minDate={addDays(
                  applicantData?.documentsToConsulateEstimatedAt,
                  1,
                )}
                maxDate={subDays(
                  applicantData?.documentsToApplicantEstimatedAt,
                  1,
                )}
              />
            </Col>
          </FormGroup>
        )}

      {isUserOperator &&
        orderTypeFieldsSettings?.enableDocumentsFromConsActualAt && (
          <FormGroup row className="mb-4 align-items-center">
            <Col sm={5} xxl={3}>
              <Label className="m-0">
                {t('order.documentsFromConsulateActualAt')}
              </Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledDatePicker
                onChange={date =>
                  formik.setFieldValue(
                    `applicants[${index}].documentsFromConsulateActualAt`,
                    date,
                  )
                }
                selected={
                  formik.values.applicants?.[index]
                    ?.documentsFromConsulateActualAt
                }
                placeholder={t('common.datePlaceholder')}
                disabled={isDisabledFields}
                placeholderText={t('common.datePlaceholder')}
                minDate={addDays(
                  applicantData?.documentsToConsulateActualAt,
                  1,
                )}
                invalid={
                  formik.errors.applicants?.[index]
                    ?.documentsToConsulateActualAt
                }
              />
            </Col>
          </FormGroup>
        )}

      {isUserOperator &&
        orderTypeFieldsSettings?.enableDocumentsToApplicantActualAt && (
          <FormGroup row className="mb-4 align-items-center">
            <Col sm={5} xxl={3}>
              <Label className="m-0">
                {t('order.documentsToApplicantActualAt')}
              </Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledDatePicker
                onChange={date =>
                  formik.setFieldValue(
                    `applicants[${index}].documentsToApplicantActualAt`,
                    date,
                  )
                }
                selected={
                  formik.values.applicants?.[index]
                    ?.documentsToApplicantActualAt
                }
                placeholder={t('common.datePlaceholder')}
                disabled={isDisabledFields}
                placeholderText={t('common.datePlaceholder')}
                minDate={addDays(
                  applicantData?.documentsFromConsulateActualAt,
                  1,
                )}
                invalid={
                  formik.errors.applicants?.[index]
                    ?.documentsToApplicantActualAt
                }
              />
            </Col>
          </FormGroup>
        )}

      {isUserOperator && orderTypeFieldsSettings?.enableGultigFrom && (
        <>
          <hr className="my-4" />

          <FormGroup row>
            <Col sm={5} xxl={3}>
              <Label className="m-0">{t('order.gultigFrom')}:</Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledDatePicker
                onChange={date =>
                  formik.setFieldValue(`applicants[${index}].gultigFrom`, date)
                }
                disabled={isDisabledFields}
                selected={formik.values.applicants?.[index]?.gultigFrom}
                placeholder={t('common.datePlaceholder')}
                placeholderText={t('common.datePlaceholder')}
                invalid={formik.errors.applicants?.[index]?.gultigFrom}
              />
            </Col>
          </FormGroup>
        </>
      )}

      {isUserOperator && orderTypeFieldsSettings?.enableGultigTo && (
        <FormGroup row>
          <Col sm={5} xxl={3}>
            <Label className="m-0">{t('order.gultigTo')}:</Label>
          </Col>

          <Col xs={12} sm={6} lg={4}>
            <StyledDatePicker
              onChange={date =>
                formik.setFieldValue(`applicants[${index}].gultigTo`, date)
              }
              disabled={isDisabledFields}
              selected={formik.values.applicants?.[index]?.gultigTo}
              placeholder={t('common.datePlaceholder')}
              placeholderText={t('common.datePlaceholder')}
              invalid={formik.errors.applicants?.[index]?.gultigTo}
            />
          </Col>
        </FormGroup>
      )}

      {isUserOperator && orderTypeFieldsSettings?.enableGrantedVisa && (
        <>
          <hr className="my-4" />

          <FormGroup row>
            <Col sm={5} xxl={3}>
              <Label className="m-0">{t('order.grantedVisa')}:</Label>
            </Col>

            <Col xs={12} sm={6} lg={4}>
              <StyledInput
                disabled={isDisabledFields}
                onChange={formik.handleChange}
                type="textarea"
                name={`applicants.[${index}].grantedVisa`}
                value={formik.values.applicants?.[index]?.grantedVisa}
                placeholder={t('order.grantedVisaPlaceholder')}
                $noResize
              />
            </Col>
          </FormGroup>
        </>
      )}

      {/* Notes box */}
      {isUserOperator && (
        <>
          <hr className="my-4" />

          <FormGroup row>
            <Col {...DEFAULT_POINTS.label.xs} sm={5} xxl={3}>
              <Label className="m-0">{t('common.comment')}</Label>
            </Col>

            <Col xs={12} sm={4}>
              <StyledInput
                disabled={isDisabledFields}
                onChange={formik.handleChange}
                type="textarea"
                name={`applicants.[${index}].comment`}
                value={formik.values.applicants?.[index]?.comment}
                placeholder={t('common.commentPlaceholder')}
                $noResize
              />
            </Col>
          </FormGroup>
        </>
      )}
    </div>
  );
};
